import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  styled,
} from "@material-ui/core";
import { FormSectionHeading } from "../../../components/Forms";
import { ColumnHeading } from "../../../components/Table";
import { UtilityIcon } from "../../../components/UtilityIcon";
import { MeterPointType } from "../../../api/meters";
import { InlineType } from "../../../components/InlineType";
import { BreakdownGroup } from "../../../api/services";
import { formatMoneyWithSign } from "../../../utils/formatMoney";

const BreakdownRow: React.FC<{
  cellProps?: any;
}> = ({ children, cellProps }) => {
  const rows = Array.isArray(children) ? children : [children];
  const left = rows[0] || null;
  const right = rows[1] || null;
  const StyledCell = styled(TableCell)(
    () =>
      cellProps || {
        paddingTop: 11,
        paddingBottom: 11,
        paddingLeft: 0,
        paddingRight: 0,
      },
  );
  return (
    <TableRow>
      <StyledCell>{left}</StyledCell>
      <StyledCell align="right">{right}</StyledCell>
    </TableRow>
  );
};

const GroupHead: React.FC<{
  type: MeterPointType;
}> = ({ type }) => {
  const label =
    type === "MPAN" ? "Electricity" : type === "MPRN" ? "Gas" : "Other";
  return (
    <BreakdownRow
      cellProps={{
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <Box display="flex" alignItems="center">
        <UtilityIcon type={type} width={16} height={16} />
        <Box ml={0.5}>
          <Typography variant="body2" component="span">
            <InlineType fontWeight="bold">{label}</InlineType>
          </Typography>
        </Box>
      </Box>
    </BreakdownRow>
  );
};

const DataRow: React.FC<{
  description: string;
  amount: string;
}> = ({ description, amount }) => (
  <BreakdownRow>
    <Box ml={2.5}>
      <Typography variant="body1">{description}</Typography>
    </Box>
    <Box mr={2}>
      <Typography variant="body1">{amount}</Typography>
    </Box>
  </BreakdownRow>
);

const TotalRow: React.FC<{
  description: string;
  amount: string;
}> = ({ description, amount }) => (
  <BreakdownRow>
    <Typography variant="body1" component="span">
      <InlineType fontWeight="bold"> {description}</InlineType>
    </Typography>
    <Box mr={2}>
      <Typography variant="body1" component="span">
        <InlineType fontWeight="bold">{amount}</InlineType>
      </Typography>
    </Box>
  </BreakdownRow>
);

const BreakdownHead: React.FC = () => (
  <BreakdownRow>
    <ColumnHeading>Description</ColumnHeading>
    <Box mr={2}>
      <ColumnHeading>Amount</ColumnHeading>
    </Box>
  </BreakdownRow>
);

const LineGroup: React.FC<{
  breakdownGroup: BreakdownGroup;
}> = ({ breakdownGroup }) => {
  const { assetType, breakdownLines } = breakdownGroup;
  return (
    <>
      <GroupHead type={assetType || ""} />
      {breakdownLines.map(({ id, description, amount, currency }) => (
        <DataRow
          key={id}
          description={description}
          amount={formatMoneyWithSign(currency, amount)}
        />
      ))}
    </>
  );
};

export const BreakdownTable: React.FC<{
  totalAmount: string;
  caption: string;
  breakdownGroups: BreakdownGroup[];
}> = ({ totalAmount, caption, breakdownGroups }) => {
  return (
    <>
      <FormSectionHeading>{caption}</FormSectionHeading>
      <Table data-testid="BillBreakdown__Table">
        <TableHead>
          <BreakdownHead />
        </TableHead>
        <TableBody>
          {breakdownGroups.map((breakdownGroup, index) => (
            <LineGroup
              key={breakdownGroup.meterPointerIdentifier || index}
              breakdownGroup={breakdownGroup}
            />
          ))}
          <TotalRow description="Total" amount={totalAmount} />
        </TableBody>
      </Table>
    </>
  );
};
